import { Box, Stack, Typography, useTheme } from "@mui/material";
import { Avatar, CheckBox, KbFileDualIcon } from "components";
import { useRef } from "react";
import { claculateTimeElapsed, getFullName } from "utilz/helpers";
import { usePermissionStore } from "controller";
import { useAtomValue, useSetAtom } from "jotai";
import { useNavigate } from "react-router-dom";
import { selectedKbItemIdsArr, toggleKbSelectionFn } from "../store";
import { KbThreeDots } from "./KbThreeDots";
import { KbRecordVisibilityChip } from "./KbRecordVisibilityChip";
import { PERMISSIONS } from "shared";

export const KbFileCard = ({
  fileDetails,
  isSelectable = true,
}: {
  fileDetails: any;
  isSelectable?: boolean;
}) => {
  const theme = useTheme();
  const navigate = useNavigate();
  const toggleSelection = useSetAtom(toggleKbSelectionFn);
  const clickTimeoutRef = useRef<NodeJS.Timeout | null>(null);

  const selectedKbItemIds = useAtomValue(selectedKbItemIdsArr);
  const isSelected =
    selectedKbItemIds?.includes(fileDetails?.mapping_id) || false;
  return (
    <Stack
      // onMouseEnter={() => setIsHovered(true)}
      // onMouseLeave={() => setIsHovered(false)}
      sx={{
        width: "196px",
        // aspectRatio: "1.07",
        border: isSelected
          ? `1px solid ${theme.palette.primary.main}`
          : `1px solid ${theme.palette.neutral["010"]}`,
        borderRadius: "8px",
        cursor: "pointer",
        p: "16px",
        boxShadow:
          "0px 0px 4px 0px rgba(0, 0, 0, 0.06), 0px 0px 2px 0px rgba(0, 0, 0, 0.04)",
        ":hover": {
          boxShadow: "-4px 9px 11px 0px rgba(0, 0, 0, 0.08)",
          "& .checkbox": {
            display: "block",
          },
          "& .three-dots": {
            display: "block",
          },
        },
        gap: "8px",
        backgroundColor: isSelected
          ? theme.palette.secondary["purple-tint-3"]
          : "transparent",

        "& .checkbox": {
          display: isSelected ? "block" : "none",
        },
        "& .three-dots": {
          display: isSelected ? "block" : "none",
        },
      }}
      onClick={() => {
        if (clickTimeoutRef.current) {
          clearTimeout(clickTimeoutRef.current);
          clickTimeoutRef.current = null;
        }
        navigate(`/app/kb/document/${fileDetails?.article_id}`);
      }}
    >
      <CardTop fileDetails={fileDetails} isSelectable={isSelectable} />
      <CardBottom fileDetails={fileDetails} />
    </Stack>
  );
};

const CardTop = ({
  fileDetails,
  isSelectable = true,
}: {
  fileDetails: any;
  isSelectable?: boolean;
}) => {
  const navigate = useNavigate();
  const theme = useTheme();
  const haveFullAccess = usePermissionStore((state) =>
    state.hasPermissions([PERMISSIONS.KB.KB_KB_RW]),
  );
  const toggleSelection = useSetAtom(toggleKbSelectionFn);
  const selectedKbItemIds = useAtomValue(selectedKbItemIdsArr);
  const isSelected =
    selectedKbItemIds?.includes(fileDetails?.mapping_id) || false;

  const handleFileOptionClick = ({ value }: { value: string }) => {
    console.log(value);
  };

  return (
    <Stack gap="8px" flexGrow={1} sx={{ width: "100%", overflow: "hidden" }}>
      <Stack
        direction={"row"}
        sx={{
          position: "relative",
          overflow: "hidden",
          justifyContent: "space-between",
          alignItems: "center",
        }}
      >
        <KbFileDualIcon
          sx={{
            width: "32px",
            height: "32px",
          }}
          htmlColor={theme.palette.primary.main}
        />
        {isSelectable && haveFullAccess && (
          <CheckBox
            className="checkbox"
            sx={{
              position: "absolute",
              top: "0px",
              right: "0px",
            }}
            checked={isSelected}
            onChange={() => toggleSelection(fileDetails?.mapping_id)}
          />
        )}
      </Stack>
      <Box
        onClick={(e) => e.stopPropagation()}
        sx={{ width: "max-content", maxWidth: "100%" }}
      >
        <Typography
          onClick={(e) =>
            navigate(`/app/kb/document/${fileDetails?.article_id}`)
          }
          variant="caption-2"
          sx={{
            display: "-webkit-box",
            WebkitLineClamp: 2,
            WebkitBoxOrient: "vertical",
            overflow: "hidden",
            minHeight: "2.4em",
            lineHeight: "1.2em",
            ":hover": {
              cursor: "pointer",
              color: theme.palette.primary.main,
              textDecoration: "underline",
            },
          }}
        >
          {fileDetails?.name}
        </Typography>
      </Box>

      {/* <EditableChip
        value={fileDetails?.name}
        onEdit={handleNameChange}
        inputSx={{
          ...theme.typography["caption-2"],
          // px: 0,
          width: "100%",
        }}
        sx={{
          overflow: "hidden",
          textOverflow: "ellipsis",
          whiteSpace: "nowrap",
          width: "100%",
          ...theme.typography["caption-2"],
        }}
        typographySx={{
          px: 0,
          ...theme.typography["caption-2"],
        }}
      /> */}
      <KbRecordVisibilityChip visibility={fileDetails?.record_visibility} />
    </Stack>
  );
};

const CardBottom = ({ fileDetails }: { fileDetails: any }) => {
  const theme = useTheme();
  const haveFullAccess = usePermissionStore((state) =>
    state.hasPermissions([PERMISSIONS.KB.KB_KB_RW]),
  );
  const selectedKbItemIds = useAtomValue(selectedKbItemIdsArr);
  const isSelected =
    selectedKbItemIds?.includes(fileDetails?.mapping_id) || false;
  const fullname = getFullName(
    fileDetails.updated_by_first_name,
    "",
    fileDetails.updated_by_last_name,
  );
  return (
    <Stack
      direction={"row"}
      bgcolor={
        isSelected ? theme.palette.secondary["purple-tint-3"] : "transparent"
      }
      sx={{ gap: "4px", alignItems: "center", justifyContent: "space-between" }}
    >
      <Stack direction={"row"} alignItems={"center"} gap={"4px"}>
        <Avatar
          variants="xs"
          fullname={fullname}
          tooltip
          src={fileDetails?.updated_by_profile_image}
        />
        <Typography variant="body-xsmall" color={theme.palette.lightBg.low}>
          Edited : {claculateTimeElapsed(Date.parse(fileDetails?.updated_at))}{" "}
          ago
        </Typography>
      </Stack>
      <Box sx={{ height: "24px", width: "24px", flexShrink: 0 }}>
        {haveFullAccess && (
          <Box className="three-dots">
            <KbThreeDots
              fileDetails={fileDetails}
              isSelected={isSelected}
              isDefaultFolder={false}
              type="file"
            />
          </Box>
        )}
      </Box>
    </Stack>
  );
};
